import styled from 'styled-components';
import {
  mqSmallMin,
  mqSmallMax,
  mqTabletMin,
  mqLargeMin,
} from 'styled/tools/media-queries';

const invertColors = (colorName: string) => {
  switch (colorName) {
    case 'var(--fill-brand)':
      return 'var(--fill-bright)';
    case 'var(--fill-bright)':
      return 'var(--text-primary)';
    default:
      return 'var(--text-primary)';
  }
};

const Button = styled.button<{
  border?: string;
  $borderRadius?: string;
  theme?: {
    color?: string;
    width?: string;
    widthSm?: string;
    widthMd?: string;
    maxWidth?: string;
    height?: string;
    fontSize?: string;
    padding?: string;
    hoverColor?: string;
    gap?: string;
    flex?: string;
    minWidth?: string;
  };
  background?: string;
  disabled?: boolean;
  maxWidth?: string;
  $isTransparent?: boolean;
  invertHover?: boolean;
  $isSelected?: boolean;
  $disableHover?: boolean;
  $textDecoration?: string;
  $disablePointerEvents?: boolean;
}>`
  border: ${({ border, $isTransparent }) =>
    border ? border : $isTransparent ? '1px solid #FFFFFF' : 0};

  border-radius: ${(props) =>
    props.$borderRadius || 'var(--flag-border-radius)'};
  display: flex;
  font-size: ${({ theme: { fontSize } }) => fontSize || '16px'};

  flex-direction: row;
  justify-content: center;
  align-items: center;

  height: ${({ theme: { height } }) => height || '48px'};
  padding: ${({ theme: { padding } }) => padding || '16px 56px'};
  gap: ${({ theme: { gap } }) => gap || '8px'};

  background-color: ${(props) =>
    props.background
      ? props.background
      : props.$isTransparent
        ? 'transparent'
        : 'var(--fill-brand)'};

  opacity: ${({ disabled }) => (disabled ? '.5;' : '')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  max-width: ${({ theme: { maxWidth } }) => maxWidth || '100%'};
  width: ${({ theme: { width } }) => width || '100%'};

  @media ${mqTabletMin} {
    min-width: ${({ theme }) => (theme?.minWidth ? theme.minWidth : 'auto')};
  }

  &&& {
    color: ${({ theme: { color: newColor } }) =>
      newColor ? newColor : '#FFFFFF'};
  }

  white-space: ${({ theme: { whiteSpace } }) =>
    whiteSpace ? 'normal' : 'nowrap'};

  @media ${mqSmallMax} {
    width: ${({ theme: { widthSm } }) => widthSm || '100%'};
  }

  @media ${mqSmallMin} {
    width: ${({ theme: { widthMd } }) => widthMd || '100%'};
  }

  @media ${mqLargeMin} {
    width: ${({ theme: { width } }) => width || '100%'};
  }

  ${({ $textDecoration }) =>
    $textDecoration && { 'text-decoration': $textDecoration }}

  ${(props) =>
    props.$disablePointerEvents && {
      'pointer-events': 'none',
    }}

  &:hover {
    background-color: ${({
      background,
      invertHover,
      $isTransparent,
      $disableHover,
      theme: { hoverBackgroundColor },
    }) => {
      if ($disableHover) {
        return null;
      }

      if (hoverBackgroundColor) {
        return `${hoverBackgroundColor} !important`;
      }
      if (invertHover) {
        return invertColors(background || 'var(--fill-brand)');
      }
      if ($isTransparent) {
        return 'var(--fill-bright)';
      }
      return background || 'var(--fill-brand)';
    }};

    color: ${({ theme, invertHover, $isTransparent, $disableHover }) => {
      if ($disableHover) {
        return null;
      }
      if (theme.hoverColor) {
        return `${theme.hoverColor} !important`;
      }
      if (invertHover) {
        return `${invertColors(theme.color || 'var(--fill-bright)')} !important`;
      }
      if ($isTransparent) {
        return `var(--text-primary) !important`;
      } else {
        return theme.color || '#FFFFFF';
      }
    }};
  }

  ${({ $isSelected }) =>
    $isSelected && {
      'background-color': 'var(--fill-bright)',
      color: `var(--text-primary) !important;`,
    }}

  transition:
    background-color,
    color 0.3s ease;
`;

export default Button;
